import { lazy, type ComponentType } from 'react';
import type { ViewRegistry } from '@/app/routing/view-registry';
import { useNavigation } from '@/app/navigation/use-navigation';

const Obra = lazy(() => import('@/features/obra/components/Obra'));
const ObraDashboard = lazy(() => import('@/features/obra/components/ObraDashboard'));
const ObraJornada = lazy(() => import('@/features/obra/components/ObraJornada'));
const ObraArea = lazy(() => import('@/features/obra/components/ObraArea'));
const ObraAmbiente = lazy(() => import('@/features/obra/components/ObraAmbiente'));
const ObraPonto = lazy(() => import('@/features/obra/components/ObraPonto'));
const ObraProduto = lazy(() => import('@/features/obra/components/ObraProduto'));
const ObraCabo = lazy(() => import('@/features/obra/components/ObraCabo'));
const ObraTubo = lazy(() => import('@/features/obra/components/ObraTubo'));
const ObraEnvolvido = lazy(() => import('@/features/obra/components/ObraEnvolvido'));
const ObraServico = lazy(() => import('@/features/obra/components/ObraServico'));
const ObraVistoria = lazy(() => import('@/features/obra/components/ObraVistoria'));
const ObraAndamento = lazy(() => import('@/features/obra/components/ObraAndamento'));
const ObraCronograma = lazy(() => import('@/features/obra/components/ObraCronograma'));
const TodasOS = lazy(() => import('@/features/os/components/TodasOS'));
const OsAgenda = lazy(() => import('@/features/os/components/OsAgenda'));

function makeOnBackOnly(Component: ComponentType<{ onBack: () => void }>) {
  return function View() {
    const { actions } = useNavigation();
    return <Component onBack={actions.goBack} />;
  };
}

function ObrasView() {
  const { obraReturnState, actions } = useNavigation();
  return (
    <Obra
      onObraSelect={(obraId: string) =>
        actions.navigateWithSelection('obras-dashboard', { obraId })
      }
      onBack={() => {
        // If an oportunidade drove us here (via onObraClick), restore that
        // drawer on return by seeding viewParams before popping history.
        if (obraReturnState) {
          actions.setViewParams({
            returnState: {
              drawerOpen: true,
              selectedOportunidadeId: obraReturnState.oportunidadeId,
            },
          });
          actions.setObraReturnState(null);
        }
        actions.goBack();
      }}
    />
  );
}

function ObrasDashboardView() {
  const { selection, actions } = useNavigation();
  return (
    <ObraDashboard
      obraId={selection.obraId ?? undefined}
      onBack={actions.goBack}
      onObraChange={(id: string) => actions.setSelectedObraId(id)}
    />
  );
}

function ObrasJornadasView() {
  const { viewParams, actions } = useNavigation();
  // ObraJornada declares initialFilterValues: InitialFilterValues (narrow
  // filter-value union). viewParams.initialFilterValues is Record<string, unknown>
  // because it spans every screen. Cast at the boundary — ObraJornada tolerates
  // unknown keys and the pre-Phase-06 path did the same via `filters: any`.
  const filterValues = viewParams?.initialFilterValues as unknown as
    | Parameters<typeof ObraJornada>[0]['initialFilterValues']
    | undefined;
  return (
    <ObraJornada
      onBack={() => {
        // Preserve oportunidade returnState across this sub-list's goBack —
        // it needs to survive for the /obras parent's onBack handler.
        const returnState = viewParams?.returnState;
        actions.setViewParams(returnState ? { returnState } : null);
        actions.goBack();
      }}
      initialFilterValues={filterValues}
      initialPeriod={viewParams?.periodFilter}
      initialOpenRecordId={viewParams?.initialOpenRecordId}
      onInitialOpenConsumed={() =>
        actions.patchViewParams({ initialOpenRecordId: undefined })
      }
    />
  );
}

function ObrasAmbientesView() {
  const { actions } = useNavigation();
  return <ObraAmbiente key="ambientes-v2-fixed-ordering" onBack={actions.goBack} />;
}

function OsAtivasView() {
  const { actions } = useNavigation();
  return (
    <TodasOS
      onBack={actions.goBack}
      onRowClick={undefined}
      onNavigateToAgenda={(filters) => {
        // TodasOS emits { advanced: Record<string, unknown>; period?: {...} };
        // OsAgenda expects initialPeriod as a string. The pre-Phase-06 code
        // bridged them via `filters: any`; we preserve the same runtime shape
        // with a narrow cast at the boundary.
        const period = filters.period as unknown as string | undefined;
        actions.navigate('os-agenda', {
          viewParams: {
            initialFilterValues: filters.advanced,
            periodFilter: period,
          },
        });
      }}
    />
  );
}

function OsAgendaView() {
  const { viewParams, actions } = useNavigation();
  return (
    <OsAgenda
      onBack={actions.goBack}
      initialFilterValues={viewParams?.initialFilterValues}
      initialPeriod={viewParams?.periodFilter}
      onClearViewParams={() => actions.setViewParams(null)}
    />
  );
}

export const obrasViews: Partial<ViewRegistry> = {
  obras: { component: ObrasView, guard: { screen: 'obras' } },
  'obras-dashboard': { component: ObrasDashboardView, guard: { screen: 'obras' } },
  'obras-jornadas': { component: ObrasJornadasView, guard: { screen: 'obras-jornadas' } },
  'obras-areas': { component: makeOnBackOnly(ObraArea), guard: { screen: 'obras-areas' } },
  'obras-ambientes': { component: ObrasAmbientesView, guard: { screen: 'obras-ambientes' } },
  'obras-pontos': { component: makeOnBackOnly(ObraPonto), guard: { screen: 'obras-pontos' } },
  'obras-produtos': {
    component: makeOnBackOnly(ObraProduto),
    guard: { screen: 'obras-produtos' },
  },
  'obras-cabos': { component: makeOnBackOnly(ObraCabo), guard: { screen: 'obras-cabos' } },
  'obras-tubos': { component: makeOnBackOnly(ObraTubo), guard: { screen: 'obras-tubos' } },
  'obras-pessoas': {
    component: makeOnBackOnly(ObraEnvolvido),
    guard: { screen: 'obras-pessoas' },
  },
  'obras-servicos': {
    component: makeOnBackOnly(ObraServico),
    guard: { screen: 'obras-servicos' },
  },
  'obras-vistorias': {
    component: makeOnBackOnly(ObraVistoria),
    guard: { screen: 'obras-vistorias' },
  },
  'obras-andamentos': {
    component: makeOnBackOnly(ObraAndamento),
    guard: { screen: 'obras-andamentos' },
  },
  'obras-cronograma': {
    component: makeOnBackOnly(ObraCronograma),
    guard: { screen: 'obras-cronograma' },
  },
  // OS view on the web channel (JOSH-91). These used `screen: 'os'`, which
  // resolves to no recurso, so they were open to every signed-in user. A
  // wrong-channel OS view shows the "use the app" notice.
  'os-ativas': { component: OsAtivasView, guard: { permission: 'OS' } },
  'os-agenda': { component: OsAgendaView, guard: { permission: 'OS' } },
};
